import React from 'react'
import { css } from '@linaria/core'
import { WhiteTheme, Paragraph, colors } from '@fs/zion-ui'
import { MediaHeadphones, DocumentStory, DocumentList } from '@fs/zion-icon'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import ViewAllCover from './ViewAllCover'

const infoTextCss = css`
  position: absolute;
  height: 100%;
  width: 100%;
  color: ${colors.gray100};

  & p {
    /* Type components in zion-ui have dumb default padding that makes positioning the element difficult */
    padding: 0;
    line-height: initial;
  }

  & h4 {
    /* Default padding on zion-ui H4 makes the text not vertically centered */
    padding-top: 0;
  }
`

const iconWrapperCss = css`
  height: 24px;
  & + * {
    margin-top: 8px;
  }
`

const centeredCss = css`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`

const cornersCss = css`
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 8px;
`

const cornerCss = css`
  display: grid;
  align-items: center;
  grid-template-columns: min-content max-content;
  grid-column-gap: 8px;
`

const countCss = css`
  height: 22px;
  padding-right: 2px;
  line-height: 22px;
`

const icons = {
  audio: MediaHeadphones,
  document: DocumentList,
  story: DocumentStory,
}

export default function TileInfo({ withImage, size, type, duration, count, viewAllCount }) {
  const [t] = useTranslation()
  const Icon = icons[type]
  const photoCount = count > 0 ? `+${count}` : undefined
  const centered = !withImage || size === 'sm'

  // Icon is aria-hidden because the aria-label on the MemoryTile takes care of it.
  const icon = Icon ? <Icon aria-hidden data-testid={`tile-icon-${type}`} /> : undefined

  const applyAriaLabel = !viewAllCount ? true : undefined
  const ariaHidden = viewAllCount ? 'true' : 'false'

  return (
    <WhiteTheme>
      {/* Aria hidden when viewCountAll because MemoryTile aria label handles "view all" for screen readers */}
      <div className={infoTextCss}>
        {centered ? (
          <div className={centeredCss}>
            <span className={iconWrapperCss}>{icon}</span>
            {type === 'audio' && duration && (
              <Paragraph
                size={size === 'lg' ? 'sm' : 'xs'}
                aria-hidden={ariaHidden}
                aria-label={applyAriaLabel && t('memory-tile.aria-label.audio-duration')}
              >
                {duration}
              </Paragraph>
            )}
            {type === 'story' && photoCount && (
              <div className={countCss}>
                <Paragraph
                  aria-hidden={ariaHidden}
                  aria-label={applyAriaLabel && t('memory-tile.aria-label.attached-photo-count')}
                  size="sm"
                >
                  {photoCount}
                </Paragraph>
              </div>
            )}
          </div>
        ) : (
          <div className={cornersCss}>
            <div className={cornerCss}>
              {icon}
              {type === 'audio' && duration && (
                <Paragraph
                  aria-hidden={ariaHidden}
                  aria-label={applyAriaLabel && t('memory-tile.aria-label.audio-duration')}
                  size="sm"
                >
                  {duration}
                </Paragraph>
              )}
            </div>
            {type === 'story' && photoCount && (
              <div className={countCss}>
                <Paragraph
                  aria-hidden={ariaHidden}
                  aria-label={applyAriaLabel && t('memory-tile.aria-label.attached-photo-count')}
                >
                  {photoCount}
                </Paragraph>
              </div>
            )}
          </div>
        )}
      </div>
      {viewAllCount > 0 && <ViewAllCover size={size} viewAllCount={viewAllCount} />}
    </WhiteTheme>
  )
}

TileInfo.propTypes = {
  /** If true, the icons and text will position themselves in the corners of size 'lg' tiles. */
  withImage: PropTypes.bool,

  /** Size of the tile. No 'md' size, in the case we add one in the future. */
  size: PropTypes.oneOf(['sm', 'lg']),

  /** The type of memory being displayed. */
  type: PropTypes.oneOf(['photo', 'document', 'story', 'audio']),

  /** Should be formatted as `mm:ss` (mins and seconds). It is not formatted for you. */
  duration: PropTypes.string,

  /** Applies to type "story" only. Indicates the number of photos attached to the story. */
  count: PropTypes.number,

  /** If provided, adds the "View All" overlay on the tile. */
  viewAllCount: PropTypes.number,
}
